import { useEffect, useRef, useState } from "react";
import { Loader2, Plus, Trash2, UserRound, Camera, Building2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { COPPER } from "./VestaAuth";
import { getAs } from "./VestaViewAs";
import { AiReadButton, AiProfiloFields } from "@/components/AiProfilo";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const asParams = () => { const a = getAs(); return a ? { as: a.id } : {}; };
const inp = "w-full rounded-lg border border-neutral-800 bg-neutral-900 px-3 py-2 text-base text-white outline-none transition-[border-color] focus:border-[#C17F59]";
const Lbl = ({ children }) => <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-neutral-400">{children}</div>;
const Sec = ({ children }) => <div className="mb-2 mt-1 text-xs font-bold uppercase tracking-widest" style={{ color: COPPER }}>{children}</div>;
const upload = (url, file) => { const fd = new FormData(); fd.append("file", file); return api.post(url, fd, { params: asParams(), headers: { "Content-Type": "multipart/form-data" } }); };

const Info = ({ l, v, id }) => (
  <div className="border-b border-neutral-900 py-1.5"><Lbl>{l}</Lbl><div className="text-sm" data-testid={id}>{v || "—"}</div></div>
);

const Picker = ({ onFile, testid, children, className }) => {
  const ref = useRef(null);
  return (
    <>
      <input ref={ref} type="file" accept="image/*" className="hidden" data-testid={`${testid}-input`} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onFile(f); }} />
      <button type="button" onClick={() => ref.current?.click()} className={className} data-testid={testid}>{children}</button>
    </>
  );
};

function Logo({ d }) {
  const [src, setSrc] = useState(null);
  const [has, setHas] = useState(d.has_logo);
  const [busy, setBusy] = useState(false);
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!has) { setSrc(null); return; }
    let u;
    api.get(`/vesta/bacheca/logo/${d.id}`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setSrc(u); }).catch(() => {});
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [has, v, d.id]);
  const onFile = async (f) => {
    setBusy(true);
    try { await upload("/vesta/logo", f); setHas(true); setV(v + 1); toast.success("Logo aggiornato"); }
    catch (e) { toast.error(e.response?.data?.detail || "Caricamento non riuscito"); }
    setBusy(false);
  };
  const del = async () => { try { await api.delete("/vesta/logo", { params: asParams() }); setHas(false); toast.success("Logo rimosso"); } catch { toast.error("Operazione non riuscita"); } };
  return (
    <div className="flex items-center gap-4" data-testid="vesta-profilo-logo">
      <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-neutral-800 bg-white">
        {busy ? <Loader2 className="h-6 w-6 animate-spin" style={{ color: COPPER }} /> : src ? <img src={src} alt="Logo" className="h-full w-full object-contain p-1.5" data-testid="vesta-profilo-logo-img" /> : <Building2 className="h-9 w-9 text-neutral-400" />}
      </div>
      <div className="space-y-1">
        <div className="font-heading text-lg font-extrabold">{d.name}</div>
        {d.data_attivazione && <div className="text-xs text-neutral-400" data-testid="vesta-profilo-partner-dal">Partner dal <span style={{ color: COPPER }}>{new Date(d.data_attivazione).toLocaleDateString("it-IT")}</span></div>}
        <div className="flex gap-3 pt-1">
          <Picker onFile={onFile} testid="vesta-profilo-logo-upload" className="text-xs font-bold uppercase tracking-widest transition-opacity hover:opacity-80" style={{ color: COPPER }}>
            <span style={{ color: COPPER }}>{has ? "Cambia logo" : "Carica logo"}</span>
          </Picker>
          {has && <button type="button" onClick={del} className="text-xs font-bold uppercase tracking-widest text-neutral-500 transition-colors hover:text-red-400" data-testid="vesta-profilo-logo-delete">Rimuovi</button>}
        </div>
      </div>
    </div>
  );
}

function Anagrafica({ d }) {
  const sede = [d.address, d.cap, d.city, d.province && `(${d.province})`].filter(Boolean).join(" ");
  const op = [d.sede_op_address, d.sede_op_city].filter(Boolean).join(" ");
  return (
    <div className="grid gap-x-6 sm:grid-cols-2" data-testid="vesta-profilo-anagrafica">
      <Info l="Ragione sociale" v={d.name} id="vesta-profilo-name" />
      <Info l="Partita IVA" v={d.piva} id="vesta-profilo-piva" />
      <Info l="Codice fiscale" v={d.codice_fiscale} />
      <Info l="Sede legale" v={sede} />
      <Info l="Sede operativa" v={op} />
    </div>
  );
}

function Foto({ r, i, onUrl }) {
  const [busy, setBusy] = useState(false);
  const onFile = async (f) => {
    setBusy(true);
    try { const res = await upload("/vesta/foto-referente", f); onUrl(res.data.url); } catch (e) { toast.error(e.response?.data?.detail || "Caricamento foto non riuscito"); }
    setBusy(false);
  };
  return (
    <Picker onFile={onFile} testid={`vesta-ref-${i}-foto`} className="group relative flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full border border-neutral-700 bg-neutral-900 transition-[border-color] hover:border-[#C17F59]">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" style={{ color: COPPER }} /> : r.foto_url ? <img src={`${BACKEND}${r.foto_url}`} alt="" className="h-full w-full object-cover" data-testid={`vesta-ref-${i}-foto-img`} /> : <Camera className="h-5 w-5 text-neutral-500" />}
    </Picker>
  );
}

function Referenti({ list, setList }) {
  const upd = (i, k, v) => setList(list.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="space-y-2" data-testid="vesta-profilo-referenti">
      {list.map((r, i) => (
        <div key={i} className="flex items-start gap-3 rounded-lg border border-neutral-900 p-2">
          <Foto r={r} i={i} onUrl={(u) => upd(i, "foto_url", u)} />
          <div className="grid flex-1 grid-cols-2 gap-2 sm:grid-cols-4">
            {["nome", "ruolo", "email", "telefono"].map((k) => (
              <input key={k} className={inp} placeholder={k[0].toUpperCase() + k.slice(1)} value={r[k] || ""} onChange={(e) => upd(i, k, e.target.value)} data-testid={`vesta-ref-${i}-${k}`} />
            ))}
          </div>
          <button type="button" onClick={() => setList(list.filter((_, j) => j !== i))} className="mt-2 text-neutral-500 transition-colors hover:text-red-400" data-testid={`vesta-ref-${i}-del`}><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <button type="button" onClick={() => setList([...list, { nome: "", ruolo: "", email: "", telefono: "" }])}
        className="flex items-center gap-1 text-xs font-bold uppercase tracking-widest transition-opacity hover:opacity-80" style={{ color: COPPER }} data-testid="vesta-ref-add">
        <Plus className="h-3.5 w-3.5" /> Aggiungi referente
      </button>
    </div>
  );
}

export const fotoKey = (c) => ((c.email || "").trim().toLowerCase() || [c.nome, c.cognome].filter(Boolean).join(" ").trim().toLowerCase());

const FierContatti = ({ list, foto, setFoto }) => !list.length ? null : (
  <div className="mb-3 space-y-2" data-testid="vesta-profilo-contatti-fier">
    {list.map((c, i) => (
      <div key={i} className="flex items-center gap-3 rounded-lg border border-neutral-900 p-2">
        <Foto r={{ foto_url: foto[fotoKey(c)] }} i={`fier-${i}`} onUrl={(u) => setFoto({ ...foto, [fotoKey(c)]: u })} />
        <div className="flex min-w-0 flex-wrap gap-x-3 text-sm text-neutral-300">
          <span className="font-semibold text-white">{[c.titolo, c.nome, c.cognome].filter(Boolean).join(" ")}</span>
          {c.ruoli && <span className="text-neutral-500">{c.ruoli}</span>}
          {c.email && <span className="break-all">{c.email}</span>}{(c.tel_cellulare || c.tel_fisso) && <span>{c.tel_cellulare || c.tel_fisso}</span>}
        </div>
      </div>
    ))}
    <p className="text-[11px] text-neutral-500">Contatti registrati da Torri: puoi aggiungere la foto, per modificare i dati contatta il tuo referente.</p>
  </div>
);

export default function ProfiloDialog({ open, onOpenChange }) {
  const [d, setD] = useState(null);
  const [f, setF] = useState(null);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const fill = (x) => { setD(x); setF({ phone: x.phone, email: x.email, website: x.website, descrizione: x.descrizione, referenti: x.referenti || [], contatti_foto: x.contatti_foto || {}, topics: x.topics || [], fondatore: x.fondatore || "", anno_fondazione: x.anno_fondazione || "", valori: x.valori || [] }); };
  useEffect(() => {
    if (!open) return;
    setD(null); setErr("");
    api.get("/vesta/profilo", { params: asParams() }).then((r) => fill(r.data)).catch((e) => setErr(e.response?.data?.detail || "Errore di caricamento"));
  }, [open]);
  const save = async () => {
    setSaving(true);
    try { const r = await api.put("/vesta/profilo", f, { params: asParams() }); fill(r.data); toast.success("Profilo aggiornato"); }
    catch (e) { toast.error(e.response?.data?.detail || "Salvataggio non riuscito"); }
    setSaving(false);
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto border-neutral-800 bg-neutral-950 text-white" data-testid="vesta-profilo-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-heading text-xl" style={{ color: COPPER }}><UserRound className="h-6 w-6" /> Profilo installatore</DialogTitle></DialogHeader>
        {err ? <p className="text-sm text-red-400" data-testid="vesta-profilo-error">{err}</p> : !d ? <Loader2 className="h-5 w-5 animate-spin" style={{ color: COPPER }} /> : (
          <div className="space-y-6">
            <Logo d={d} />
            <div><Sec>Anagrafica</Sec><Anagrafica d={d} /></div>
            <div>
              <Sec>Contatti aziendali</Sec>
              <div className="grid gap-3 sm:grid-cols-3">
                <div><Lbl>Telefono</Lbl><input className={inp} value={f.phone} onChange={set("phone")} data-testid="vesta-profilo-phone" /></div>
                <div><Lbl>Email</Lbl><input className={inp} value={f.email} onChange={set("email")} data-testid="vesta-profilo-email" /></div>
                <div><Lbl>Sito web</Lbl><input className={inp} value={f.website} onChange={set("website")} placeholder="www…" data-testid="vesta-profilo-website" />
                  <AiReadButton website={f.website} generate={(w) => api.post("/vesta/profilo/ai", { website: w }, { params: asParams(), timeout: 120000 }).then((r) => r.data)}
                    onResult={(r) => setF((o) => ({ ...o, descrizione: r.descrizione, topics: r.topics, fondatore: r.fondatore || o.fondatore, anno_fondazione: r.anno_fondazione || o.anno_fondazione, valori: r.valori.length ? r.valori : o.valori }))} />
                </div>
              </div>
            </div>
            <div><Sec>Referenti</Sec><FierContatti list={d.contatti} foto={f.contatti_foto} setFoto={(m) => setF({ ...f, contatti_foto: m })} /><Referenti list={f.referenti} setList={(l) => setF({ ...f, referenti: l })} /></div>
            <div>
              <Sec>Descrizione e competenze</Sec>
              <textarea rows={4} className={inp} value={f.descrizione} onChange={set("descrizione")} placeholder="Raccontaci la tua azienda: servizi, specializzazioni, zone coperte…" data-testid="vesta-profilo-descrizione" />
              <div className="mt-4"><AiProfiloFields f={f} setF={setF} /></div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="text-[11px] text-neutral-500" data-testid="vesta-profilo-updated">{d.profilo_vesta_at ? `Ultimo aggiornamento ${new Date(d.profilo_vesta_at).toLocaleString("it-IT")}${d.profilo_vesta_by ? ` · ${d.profilo_vesta_by}` : ""}` : "Le modifiche sono condivise subito con Torri."}</span>
              <button type="button" onClick={save} disabled={saving} data-testid="vesta-profilo-save"
                className="rounded-full px-6 py-2 text-xs font-bold uppercase tracking-widest text-black transition-[opacity,transform] hover:-translate-y-0.5 disabled:opacity-50" style={{ background: COPPER }}>
                {saving ? "Salvataggio…" : "Salva profilo"}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
